import { useCallback, useEffect, useMemo, useState } from 'react';
import { ILocation } from '../types';
import { getLocations } from '../services';

const defaultLocation = { lat: 37.7749, lng: -122.4194 };

export const useLocations = () => {
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [radius, setRadius] = useState(30);
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral>();
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete>();

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query.toLowerCase());
  }, []);

  const handlePlaceChanged = useCallback(() => {
    const place = autocomplete?.getPlace();
    if (place?.geometry && place?.geometry) {
      const latitude = place.geometry?.location?.lat();
      const longitude = place.geometry?.location?.lng();

      if (latitude && longitude) {
        const coordinates = {
          lat: latitude,
          lng: longitude
        };

        setUserLocation(coordinates);
      }
    }
  }, [autocomplete]);

  const filteredLocations = useMemo(
    () => locations.filter((location) => location.title.toLowerCase().includes(searchQuery)),
    [locations, searchQuery]
  );

  const radiusInMiles = useMemo(() => {
    return radius * 1620;
  }, [radius]);

  const locationsInArea = filteredLocations.filter((location) => {
    if (!google?.maps?.geometry?.spherical || !userLocation) {
      return false;
    }

    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(userLocation),
      new google.maps.LatLng(location.coordinates)
    );
    return distance <= radiusInMiles;
  });

  useEffect(() => {
    // Fetch user's location using the Geolocation API
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error fetching location:', error);
          // Fallback to a default location (e.g., San Francisco)
          setUserLocation(defaultLocation);
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
      // Fallback to a default location
      setUserLocation(defaultLocation);
    }
  }, []);

  useEffect(() => {
    getLocations().then((resLocations) => setLocations(resLocations));
  }, []);

  return {
    locationsInArea,
    radius,
    userLocation,
    setRadius,
    handleSearch,
    radiusInMiles,
    handlePlaceChanged,
    setAutocomplete
  };
};
